function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
}

// tw-animate-css accordion-down/up keyframes use 200ms duration
const ACCORDION_ANIMATION_MS = 200
const ACCORDION_SETTLE_FALLBACK_MS = ACCORDION_ANIMATION_MS + 100

function getAccordionScrollSafeTop(element: HTMLElement) {
  const scrollMarginTop = Number.parseFloat(getComputedStyle(element).scrollMarginTop)

  if (Number.isFinite(scrollMarginTop) && scrollMarginTop > 0) {
    return scrollMarginTop
  }

  const headerBottom = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--mobile-header-bottom'),
  )

  return (Number.isFinite(headerBottom) ? headerBottom : 80) + 12
}

function scrollAccordionItemToTopOnMobile(element: HTMLElement | null) {
  if (!element || !isMobileViewport()) return

  const top =
    element.getBoundingClientRect().top + window.scrollY - getAccordionScrollSafeTop(element)

  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

function getAccordionContentPanel(itemEl: HTMLElement | null): HTMLElement | null {
  return itemEl?.querySelector<HTMLElement>('[data-slot="accordion-content"]') ?? null
}

function afterNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

function waitForPanelAnimationEnd(panel: HTMLElement | null, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!panel) {
      resolve()
      return
    }

    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    let fallbackId = 0

    const cleanup = () => {
      panel.removeEventListener('animationend', onAnimationEnd)
      signal?.removeEventListener('abort', onAbort)
      window.clearTimeout(fallbackId)
    }

    const onAbort = () => {
      cleanup()
      reject(new DOMException('Aborted', 'AbortError'))
    }

    const onAnimationEnd = (event: AnimationEvent) => {
      if (event.target !== panel) return
      cleanup()
      resolve()
    }

    fallbackId = window.setTimeout(() => {
      cleanup()
      resolve()
    }, ACCORDION_SETTLE_FALLBACK_MS)

    panel.addEventListener('animationend', onAnimationEnd)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function waitForAccordionSettle({
  closeItemId,
  openItemId,
  signal,
}: {
  closeItemId?: string
  openItemId: string
  signal?: AbortSignal
}): Promise<void> {
  const openPanel = getAccordionContentPanel(document.getElementById(openItemId))
  const panels: (HTMLElement | null)[] = [openPanel]

  if (closeItemId && closeItemId !== openItemId) {
    panels.push(getAccordionContentPanel(document.getElementById(closeItemId)))
  }

  return Promise.all(panels.map((panel) => waitForPanelAnimationEnd(panel, signal))).then(
    () => undefined,
  )
}

export function scrollExpandedAccordionItemOnMobile({
  closeItemId,
  openItemId,
  signal,
}: {
  closeItemId?: string
  openItemId: string
  signal?: AbortSignal
}) {
  if (!isMobileViewport()) return Promise.resolve()

  return afterNextPaint()
    .then(() => {
      if (signal?.aborted) return
      return waitForAccordionSettle({ closeItemId, openItemId, signal })
    })
    .then(() => {
      if (signal?.aborted) return
      scrollAccordionItemToTopOnMobile(document.getElementById(openItemId))
    })
}
